import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';

import { organizationOrdersTableColumns } from 'components/dashboards';
import { useOrders } from 'hooks/useDashboard';

import paths from 'routes/paths';

import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ExportModal from 'components/common/ExportModal';
import DonationsFilterForm from './DonationsFilterForm';
import useSessionState from 'hooks/useSessionState';

const exportColumns = [
  { accessor: 'date', Header: 'Date' },
  { accessor: 'reference', Header: 'Reference' },
  { accessor: 'first_name', Header: 'First Name' },
  { accessor: 'surname', Header: 'Surname' },
  { accessor: 'email', Header: 'Email' },
  { accessor: 'billing_type', Header: 'Monthly / One Off' },
  { accessor: 'donation_type', Header: 'Donation Type' },
  { accessor: 'package', Header: 'Package' },
  { accessor: 'payment_method', Header: 'Payment Method' },
  { accessor: 'donation_source', Header: 'Source' },
  { accessor: 'payment_status', Header: 'Payment Status' },
  { accessor: 'cancellation_reason', Header: 'Cancellation Reason' },
  { accessor: 'cancellation_notes', Header: 'Cancellation Notes' },
  { accessor: 'amount', Header: 'Amount' }
];

// Filter-bar values -> query filters
const toFilters = data => ({
  ...(data.date?.from && { dateFrom: data.date.from }),
  ...(data.date?.to && { dateTo: data.date.to }),
  // Billing type (one-off/monthly) filters server-side on is_recurring;
  // selecting both (or neither) means no filter
  ...(data.donationType?.length === 1 && { type: data.donationType[0] }),
  ...(data.donorName?.trim() && { donorName: data.donorName.trim() }),
  ...(data.type?.length && { donationType: data.type }),
  ...(data.package?.length && { package: data.package }),
  ...(data.paymentMethod?.length && { paymentMethod: data.paymentMethod }),
  ...(data.donationSource?.length && {
    donationSource: data.donationSource
  }),
  ...(data.cancellationReason?.length && {
    cancellationReason: data.cancellationReason
  })
});

function DonationsPage() {
  // The filter bar's values are kept for this tab, so coming back to the
  // list shows the same filters and results
  const [savedFilterForm, setSavedFilterForm] = useSessionState(
    'filters:donations',
    null
  );
  const [filters, setFilters] = useState(() =>
    savedFilterForm ? toFilters(savedFilterForm) : {}
  );
  const [showExportModal, setShowExportModal] = useState(false);
  const { orders, ordersLoading } = useOrders(filters);

  const paymentMethodOptions = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    const seen = new Set();
    return orders.reduce((acc, order) => {
      const name = order.payment_method?.payment_name;
      if (name && !seen.has(name)) {
        seen.add(name);
        acc.push({ label: name, value: name });
      }
      return acc;
    }, []);
  }, [orders]);

  // All list filters are multi-select: an order matches when it matches ANY
  // of the selected values for each active filter
  const filteredOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    let result = orders;
    if (filters.donorName) {
      const term = filters.donorName.toLowerCase();
      result = result.filter(order =>
        `${order.donar?.first_name || ''} ${order.donar?.last_name || ''}`
          .toLowerCase()
          .includes(term)
      );
    }
    if (filters.donationType?.length) {
      result = result.filter(order =>
        order.order_details.some(detail =>
          filters.donationType.includes(detail.donation_type_id)
        )
      );
    }
    if (filters.package?.length) {
      result = result.filter(order =>
        order.order_details.some(detail =>
          filters.package.includes(detail.package_id)
        )
      );
    }
    if (filters.paymentMethod?.length) {
      result = result.filter(order =>
        filters.paymentMethod.includes(order.payment_method?.payment_name)
      );
    }
    if (filters.donationSource?.length) {
      result = result.filter(order =>
        filters.donationSource.includes(order.donation_source)
      );
    }
    if (filters.cancellationReason?.length) {
      result = result.filter(
        order =>
          order.payment_status === 'refunded' &&
          order.refunds?.some(r =>
            filters.cancellationReason.includes(r.reason)
          )
      );
    }
    return result;
  }, [
    orders,
    filters.donorName,
    filters.donationType,
    filters.package,
    filters.paymentMethod,
    filters.donationSource,
    filters.cancellationReason
  ]);

  const hasDetailFilters =
    filters.donationType?.length || filters.package?.length;

  const getMatchingDetails = order => {
    if (!hasDetailFilters) return order.order_details;
    return order.order_details.filter(detail => {
      if (
        filters.donationType?.length &&
        !filters.donationType.includes(detail.donation_type_id)
      )
        return false;
      if (
        filters.package?.length &&
        !filters.package.includes(detail.package_id)
      )
        return false;
      return true;
    });
  };

  const totalAmount = useMemo(() => {
    if (!hasDetailFilters) {
      return filteredOrders.reduce(
        (sum, order) => sum + (order.donation_amount ?? 0),
        0
      );
    }
    return filteredOrders.reduce((sum, order) => {
      const matched = getMatchingDetails(order);
      return sum + matched.reduce((s, d) => s + (d.amount ?? 0), 0);
    }, 0);
  }, [filteredOrders, hasDetailFilters]);

  const tableData = useMemo(() => {
    return filteredOrders.map(order => {
      let primaryEmail = '';
      const donor = order.donar;

      if (donor?.email?.length) {
        primaryEmail =
          donor.email?.find(email => email.isPrimary)?.email ??
          donor.email?.[0]?.email ??
          donor.primary_email ??
          '';
      } else if (donor?.primary_email) {
        primaryEmail = donor.primary_email;
      }

      const matchedDetails = getMatchingDetails(order);

      return {
        simple_id: order.simple_id,
        id: order.id,
        donar_id: donor?.id,
        name: donor ? (
          <Link to={paths.donorDetails.replace(':id', donor.id)}>
            {donor.first_name} {donor.last_name}
          </Link>
        ) : (
          'Unknown Donor'
        ),
        email: primaryEmail,
        created_at: order.created_at,
        order_details: (
          <ul className="list-unstyled mb-0">
            {matchedDetails.map((detail, index) => (
              <li key={index}>
                {detail.package?.package_title || 'Quick Donation'} |{' '}
                {detail.billing_condition} |{' '}
                {detail.donation_type?.donation_type_name}
              </li>
            ))}
          </ul>
        ),
        is_recurring: order.is_recurring,
        payment_method: order.payment_method?.payment_name,
        donation_source: order.donation_source,
        payment_status: order.payment_status,
        score: 0,
        variant: 'success',
        amount: hasDetailFilters
          ? matchedDetails.reduce((s, d) => s + (d.amount ?? 0), 0)
          : order.donation_amount
      };
    });
  }, [filteredOrders, hasDetailFilters]);

  const exportData = useMemo(() => {
    return filteredOrders.flatMap(order => {
      const donor = order.donar;
      let primaryEmail = '';
      if (donor?.email?.length) {
        primaryEmail =
          donor.email?.find(e => e.isPrimary)?.email ??
          donor.email?.[0]?.email ??
          donor.primary_email ??
          '';
      } else if (donor?.primary_email) {
        primaryEmail = donor.primary_email;
      }

      const details = getMatchingDetails(order);

      return details.map(detail => ({
        date: order.created_at
          ? new Date(order.created_at).toLocaleDateString()
          : '',
        reference: order.simple_id ?? '',
        first_name: donor?.first_name ?? '',
        surname: donor?.last_name ?? '',
        email: primaryEmail,
        billing_type: detail.billing_condition ?? '',
        donation_type: detail.donation_type?.donation_type_name ?? '',
        package: detail.package?.package_title ?? 'Quick Donation',
        payment_method: order.payment_method?.payment_name ?? '',
        donation_source: order.donation_source === 'phone' ? 'Phone' : 'Online',
        payment_status: order.payment_status ?? '',
        cancellation_reason: order.refunds?.[0]?.reason ?? '',
        cancellation_notes: order.refunds?.[0]?.notes ?? '',
        amount: detail.amount ?? ''
      }));
    });
  }, [filteredOrders, hasDetailFilters]);

  const onFilterClick = data => {
    setSavedFilterForm(data);
    setFilters(toFilters(data));
  };

  return (
    <div>
      <div className="card shadow-none border mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-end">
            <div className="flex-grow-1">
              <DonationsFilterForm
                onFilter={onFilterClick}
                initialValues={savedFilterForm}
                paymentMethodOptions={paymentMethodOptions}
              />
            </div>
            <div
              className="ms-3 rounded-3 text-center px-3 py-2"
              style={{
                backgroundColor: 'var(--falcon-primary-bg-subtle, #e8f0fe)',
                minWidth: 130,
                marginTop: '1.5rem'
              }}
            >
              <small
                className="text-uppercase fw-semibold text-600 d-block mb-1"
                style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
              >
                Total Donations
              </small>
              <h5 className="mb-0 text-primary fw-bold">
                £
                {totalAmount.toLocaleString('en-GB', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </h5>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-2 d-flex justify-content-end">
        <Link to={paths.addDonation}>
          <Button>Add Donation</Button>
        </Link>
      </div>

      <CustomAdvanceTable
        data={tableData ?? []}
        columns={organizationOrdersTableColumns}
        loading={ordersLoading}
        title="Donations"
        hasExport
        handleExport={() => setShowExportModal(true)}
      />

      <ExportModal
        data={exportData}
        columns={exportColumns}
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        fileName="Donations"
      />
    </div>
  );
}

export default DonationsPage;
